import { Checkbox, IconButton, toast } from "@carbon6/c6-frontend-design";
import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import {
  getDatasetDirectoryTree,
  listDatasetEpisodeFiles,
  listDatasetExtraFiles,
  listDatasetMetaFiles,
  type DirectoryTree,
} from "@/api";
import { HatTextModal } from "@/components/modal";
import { HatTextTooltip } from "@/components/tooltip";
import { createLogger } from "@/logging";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { datasetDownloadStore } from "./datasetDownloadStore";
import { pickDownloadDirectory, supportsDirectoryDownload } from "./directoryWriter";
import type { DownloadSourceFiles } from "./downloadPlan";
import { buildDownloadPreview } from "./downloadPreview";
import styles from "./DatasetDownload.module.css";

const logger = createLogger("components/dataset/download");

const UNSUPPORTED_HINT =
  "여러 파일을 원본 폴더 구조 그대로 받으려면 Chrome 또는 Edge 가 필요합니다. 파일 하나씩 받는 것은 모든 브라우저에서 됩니다.";

// 미리보기용 표본. 서명 없이 첫 페이지만 읽는다(행당 서명 비용이 없게 with_download_url 은 끈다).
// 폴더마다 예시를 채울 만큼 충분히 받는다. 첫 페이지는 에피소드 순이므로 앞쪽 에피소드 몇 개가
// 전부 들어오고, 그 안에 모든 채널 폴더가 나온다.
const PREVIEW_SCAN_SIZE = 100;

export type DatasetDownloadActionProps = {
  datasetUuid: string;
};

type PreviewState = "idle" | "loading" | "ready" | "error";

// 미리보기의 두 출처. 폴더 구성·파일 수는 트리가, 예시 경로는 표본이 제공한다.
type PreviewSource = {
  tree: DirectoryTree;
  sample: DownloadSourceFiles;
};

// 이 표면은 데이터셋 전체만 받는다. 계획·진행 계층은 폴더 스코프(`prefix`)도 모델링한다
// (`buildDownloadPlan` 의 경계 판정). 그러나 UI 진입점이 이 하나뿐이므로 값은 항상 null 이다.
const WHOLE_DATASET_PREFIX = null;

/**
 * 대량 다운로드 트리거. 저장 폴더를 고르고, 그 아래 저장될 경로를 확인한 뒤 실행한다.
 *
 * 진입점은 상세 헤더 하나뿐이다(데이터셋 전체). 실제 실행과 진행 표시는 `DatasetDownloadPanel`
 * 이 소유한다.
 */
export function DatasetDownloadAction({ datasetUuid }: DatasetDownloadActionProps): ReactNode {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [includeExcluded, setIncludeExcluded] = useState(false);
  const [directory, setDirectory] = useState<FileSystemDirectoryHandle | null>(null);
  // 미리보기 출처는 폴더를 고를 때 한 번만 조회한다. excluded 토글이 바뀌면 로컬에서 다시 계산한다.
  const [source, setSource] = useState<PreviewSource | null>(null);
  const [previewState, setPreviewState] = useState<PreviewState>("idle");
  const excludedToggleId = useId();
  const supported = supportsDirectoryDownload();

  // 모달을 닫으면 선택을 버린다. 다시 열었을 때 이전 폴더가 남아 있으면 사용자가 오해한다.
  useEffect(() => {
    if (confirmOpen) return;
    setDirectory(null);
    setSource(null);
    setPreviewState("idle");
  }, [confirmOpen]);

  const preview = useMemo(
    () =>
      source === null
        ? null
        : buildDownloadPreview(source.tree, source.sample, { includeExcluded }),
    [source, includeExcluded],
  );

  const handlePick = (): void => {
    // showDirectoryPicker 는 이 클릭의 transient user activation 안에서 열려야 한다.
    // 앞에 await 를 하나라도 넣으면 activation 이 소실되어 실패한다. 그래서 동기로 호출한다.
    void pickDownloadDirectory()
      .then(async (picked) => {
        // null 은 사용자가 폴더 선택을 취소했다는 뜻이다. 오류가 아니므로 조용히 끝낸다.
        if (picked === null) return;
        setDirectory(picked);
        setPreviewState("loading");
        try {
          setSource(await loadPreviewSource(datasetUuid));
          setPreviewState("ready");
        } catch (error) {
          // 미리보기는 부가 정보이므로 실패해도 다운로드를 막지 않는다.
          logger.warn("download preview load failed", error);
          setPreviewState("error");
        }
      })
      .catch((error: unknown) => {
        logger.error("download directory picker failed", error);
        toast.error("저장할 폴더를 열지 못했습니다. 잠시 후 다시 시도해 주세요.");
      });
  };

  const handleConfirm = (): void => {
    // 폴더 이름은 트리가 주는 값이다. 그래서 source 없이는 저장 위치를 정할 수 없다.
    if (directory === null || source === null) return;
    setConfirmOpen(false);
    datasetDownloadStore.start(datasetUuid, {
      directory,
      folderName: source.tree.download_folder_name,
      prefix: WHOLE_DATASET_PREFIX,
      includeExcluded,
    });
  };

  // 받을 수 없는 사유는 브라우저 미지원 하나뿐이다. 데이터셋 상태(파생 산출 중)는 받는 내용을
  // 바꾸지 않는다.
  const disabledHint = supported ? null : UNSUPPORTED_HINT;
  const button = (
    <IconButton
      text="다운로드"
      size="md"
      variant="surface"
      disabled={disabledHint !== null}
      title={disabledHint === null ? "원본 폴더 구조 그대로 내려받습니다." : undefined}
      onClick={() => setConfirmOpen(true)}
    />
  );

  return (
    <>
      {disabledHint === null ? (
        button
      ) : (
        // disabled 버튼은 포인터 이벤트를 받지 못해 툴팁이 뜨지 않는다. 그래서 툴팁을 래퍼에 연결한다.
        <HatTextTooltip text={disabledHint}>
          <span className={styles.disabledWrap}>{button}</span>
        </HatTextTooltip>
      )}
      <HatTextModal
        open={confirmOpen}
        label="파일을 내려받습니다."
        okText="다운로드"
        okDisabled={directory === null || source === null}
        size="md"
        content={
          <div className={styles.confirmBody}>
            <IconButton
              text={directory === null ? "폴더 선택" : "폴더 변경"}
              size="md"
              variant="surface"
              onClick={handlePick}
            />
            {directory === null ? null : (
              <>
                <section className={styles.preview} aria-label="저장 위치 미리보기">
                  <p className={styles.previewRoot}>
                    {directory.name}/{source === null ? "" : `${source.tree.download_folder_name}/`}
                  </p>
                  {previewState === "loading" ? (
                    <LoadingState size="sm" mode="plain">
                      미리보기를 불러오고 있습니다.
                    </LoadingState>
                  ) : source === null ? (
                    <EmptyState size="sm" mode="plain" tone="error">
                      저장 위치를 확인하지 못했습니다. 폴더를 다시 선택해 주세요.
                    </EmptyState>
                  ) : preview === null ? (
                    <EmptyState size="sm" mode="plain" tone="error">
                      미리보기를 불러오지 못했습니다. 다운로드는 그대로 진행할 수 있습니다.
                    </EmptyState>
                  ) : (
                    <ul className={styles.previewTree}>
                      {preview.groups.map((group) => (
                        <li key={group.folder ?? ""}>
                          {group.folder === null ? null : (
                            <p className={styles.previewFolder}>
                              <span>{group.folder}/</span>
                              <span className={styles.previewCount}>
                                {group.fileCount.toLocaleString()}개
                              </span>
                            </p>
                          )}
                          <ul className={styles.previewFiles}>
                            {group.samplePaths.map((path) => (
                              <li key={path}>{path}</li>
                            ))}
                            {group.hasMore ? <li>…</li> : null}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
                {source === null ? null : renamedFolderNote(source.tree)}
                {preview === null ? null : (
                  <p className={styles.scope}>
                    {downloadScopeText(preview.totalFiles, includeExcluded)}
                  </p>
                )}
              </>
            )}
            <div className={styles.confirmToggle}>
              <Checkbox
                id={excludedToggleId}
                checked={includeExcluded}
                onChange={(event) => setIncludeExcluded(event.target.checked)}
              />
              <label htmlFor={excludedToggleId}>제외된 에피소드의 파일도 포함</label>
            </div>
          </div>
        }
        onOkPress={handleConfirm}
        onCancelPress={() => setConfirmOpen(false)}
      />
    </>
  );
}

/**
 * 이름이 폴더용으로 바뀌었으면 그 사실을 알린다. 받은 뒤에 알면 늦다.
 *
 * 서버가 변환값과 원문을 함께 준다. 그래서 두 값을 비교해 판정한다(별도 플래그 없음).
 */
function renamedFolderNote(tree: DirectoryTree): ReactNode {
  if (tree.dataset_display_name === tree.download_folder_name) return null;
  return (
    <p className={styles.previewNote}>
      Dataset 이름 “{tree.dataset_display_name}”에는 폴더로 쓸 수 없는 문자가 있어 “
      {tree.download_folder_name}”로 저장합니다.
    </p>
  );
}

/**
 * 미리보기 출처를 모은다. 디렉터리 트리 하나와 세 목록의 첫 페이지다.
 *
 * 네 요청을 함께 보내므로 왕복은 1회다. 트리는 파일을 담지 않는다(크기는 폴더 수에 비례). 그래서
 * 파일이 수천 개여도 한 번에 온다. 목록은 서명을 켜지 않으므로 행당 서명 비용이 없다.
 *
 * 트리만 필수다. 트리는 저장할 폴더 이름의 출처이므로, 없으면 저장 위치를 정할 수 없다. 예시
 * 경로용 표본 세 목록은 실패해도 빈 목록으로 진행한다.
 */
async function loadPreviewSource(datasetUuid: string): Promise<PreviewSource> {
  const query = { page: 1, size: PREVIEW_SCAN_SIZE };
  const [tree, episodes, metas, extras] = await Promise.all([
    getDatasetDirectoryTree(datasetUuid),
    // 서버는 episode 목록의 page 를 cursor 로 나눈다. 첫 page 의 요청에는 after 가 없다.
    sampleItems(listDatasetEpisodeFiles(datasetUuid, { size: PREVIEW_SCAN_SIZE })),
    sampleItems(listDatasetMetaFiles(datasetUuid, query)),
    sampleItems(listDatasetExtraFiles(datasetUuid, query)),
  ]);
  return {
    tree,
    sample: { episodeFiles: episodes, metaFiles: metas, extraFiles: extras },
  };
}

// 표본 하나를 조회한다. 실패하면 빈 목록을 쓴다(예시가 줄 뿐 다운로드를 막지 않는다).
async function sampleItems<T>(request: Promise<{ items: T[] }>): Promise<T[]> {
  try {
    return (await request).items;
  } catch (error) {
    logger.warn("download preview sample failed", error);
    return [];
  }
}

/**
 * 미리보기 아래 한 줄. 이 실행이 무엇을 받는지 설명한다.
 *
 * 트리의 총량은 데이터셋 전체 수다. 그래서 제외된 에피소드를 빼고 받을 때는 상한이다. 그 차이를
 * 숫자로 감추지 않고 문장으로 밝힌다.
 */
function downloadScopeText(totalFiles: number, includeExcluded: boolean): string {
  const total = totalFiles.toLocaleString();
  return includeExcluded
    ? `파일 ${total}개를 모두 내려받습니다.`
    : `제외된 에피소드의 파일을 빼고 내려받습니다. Dataset 전체는 파일 ${total}개입니다.`;
}
